import { template } from "@ember/template-compiler";
import Component from "@glimmer/component";
import { cached, tracked } from "@glimmer/tracking";
import ClassicComponent from "@ember/component";
import { concat } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import { service } from "@ember/service";
import { and, not, or } from "truth-helpers";
import ConditionalInElement from "discourse/components/conditional-in-element";
import DButton from "discourse/components/d-button";
import concatClass from "discourse/helpers/concat-class";
import { disableBodyScroll } from "discourse/lib/body-scroll-lock";
import swipe from "discourse/modifiers/swipe";
import trapTab from "discourse/modifiers/trap-tab";
import { bind } from "discourse-common/utils/decorators";
export const CLOSE_INITIATED_BY_BUTTON = "initiatedByCloseButton";
export const CLOSE_INITIATED_BY_ESC = "initiatedByESC";
export const CLOSE_INITIATED_BY_CLICK_OUTSIDE = "initiatedByClickOut";
export const CLOSE_INITIATED_BY_MODAL_SHOW = "initiatedByModalShow";
export const CLOSE_INITIATED_BY_SWIPE_DOWN = "initiatedBySwipeDown";
const FLASH_TYPES = [
    "success",
    "error",
    "warning",
    "info"
];
const ANIMATE_MODAL_DURATION = 250;
const MIN_SWIPE_THRESHOLD = -5;
export default class DModal extends Component {
    @service
    modal;
    @service
    site;
    @service
    appEvents;
    @tracked
    wrapperElement;
    @tracked
    animating = false;
    @action
    setupModal(element1) {
        document.documentElement.addEventListener("keydown", this.handleDocumentKeydown);
        this.appEvents.on("keyboard-visibility-change", this.handleKeyboardVisibilityChange);
        if (this.site.mobileView) {
            disableBodyScroll(element1.querySelector(".d-modal__body"));
            this.animating = true;
            element1.animate([
                {
                    transform: "translateY(100%)"
                },
                {
                    transform: "translateY(0)"
                }
            ], {
                duration: ANIMATE_MODAL_DURATION,
                easing: "ease",
                fill: "forwards"
            }).finished.then(()=>{
                this.animating = false;
            });
        }
        this.wrapperElement = element1;
    }
    @action
    cleanupModal() {
        document.documentElement.removeEventListener("keydown", this.handleDocumentKeydown);
        this.appEvents.off("keyboard-visibility-change", this.handleKeyboardVisibilityChange);
    }
    get dismissable() {
        if (!this.args.closeModal) {
            return false;
        } else if ("dismissable" in this.args) {
            return this.args.dismissable;
        } else {
            return true;
        }
    }
    shouldTriggerClickOnEnter(event1) {
        if (this.args.submitOnEnter === false) {
            return false;
        }
        // skip when in a form or a textarea element
        if (event1.target.closest("form") || document.activeElement?.nodeName === "TEXTAREA") {
            return false;
        }
        return true;
    }
    @action
    async handleSwipe(state1) {
        if (!this.site.mobileView) {
            return;
        }
        if (this.animating) {
            return;
        }
        if (state1.deltaY < 0) {
            await this.#animateWrapperPosition(Math.abs(state1.deltaY));
            return;
        }
    }
    @action
    handleSwipeEnded(state1) {
        if (!this.site.mobileView) {
            return;
        }
        if (this.animating) {
            // if the modal is animating we don't want to risk resetting the position
            // as the user releases the swipe at the same time
            return;
        }
        if (state1.deltaY < MIN_SWIPE_THRESHOLD) {
            this.wrapperElement.querySelector(".d-modal__container").style.transform = `translateY(${Math.abs(state1.deltaY)}px)`;
            this.closeModal(CLOSE_INITIATED_BY_SWIPE_DOWN);
        }
    }
    @action
    handleWrapperClick(e1) {
        if (e1.button !== 0) {
            return; // Non-default mouse button
        }
        if (!this.dismissable) {
            return;
        }
        return this.closeModal(CLOSE_INITIATED_BY_CLICK_OUTSIDE);
    }
    @action
    async closeModal(initiatedBy1) {
        if (!this.args.closeModal) {
            return;
        }
        if (this.site.mobileView) {
            this.animating = true;
            this.#animateBackdropOpacity(window.innerHeight);
            await this.wrapperElement.animate([
                // hidding first ms to avoid flicker
                {
                    visibility: "hidden",
                    offset: 0
                },
                {
                    visibility: "visible",
                    offset: 0.01
                },
                {
                    transform: "translateY(100%)",
                    offset: 1
                }
            ], {
                duration: ANIMATE_MODAL_DURATION,
                fill: "forwards"
            }).finished;
            this.animating = false;
        }
        this.args.closeModal({
            initiatedBy: initiatedBy1
        });
    }
    @action
    handleDocumentKeydown(event1) {
        if (this.args.hidden) {
            return;
        }
        if (event1.key === "Escape" && this.dismissable) {
            event1.stopPropagation();
            this.closeModal(CLOSE_INITIATED_BY_ESC);
        }
        if (event1.key === "Enter" && this.shouldTriggerClickOnEnter(event1)) {
            this.wrapperElement.querySelector(".d-modal__footer .btn-primary")?.click();
            event1.preventDefault();
        }
    }
    @action
    handleCloseButton() {
        this.closeModal(CLOSE_INITIATED_BY_BUTTON);
    }
    @action
    validateFlashType(type1) {
        if (type1 && !FLASH_TYPES.includes(type1)) {
            throw `@flashType must be one of ${FLASH_TYPES.join(", ")}`;
        }
    }
    // Could be optimised to remove classic component once RFC389 is implemented
    // https://rfcs.emberjs.com/id/0389-dynamic-tag-names
    @cached
    get dynamicElement() {
        const tagName1 = this.args.tagName || "div";
        if (![
            "div",
            "form"
        ].includes(tagName1)) {
            throw `@tagName must be form or div`;
        }
        return class WrapperComponent extends ClassicComponent {
            tagName = tagName1;
        };
    }
    @bind
    handleKeyboardVisibilityChange(visible1) {
        if (visible1) {
            window.scrollTo(0, 0);
        }
    }
    #animateBackdropOpacity(position1) {
        const backdrop1 = this.wrapperElement.nextElementSibling;
        if (!backdrop1) {
            return;
        }
        // 85vh is the max height of the modal
        const opacity1 = 1 - position1 / (window.innerHeight * 0.85);
        requestAnimationFrame(()=>{
            backdrop1.style.setProperty("opacity", Math.max(0, Math.min(opacity1, 0.6)), "important");
        });
    }
    async #animateWrapperPosition(position1) {
        this.#animateBackdropOpacity(position1);
        await this.wrapperElement.animate([
            {
                transform: `translateY(${position1}px)`
            }
        ], {
            fill: "forwards"
        }).finished;
    }
    static{
        template(`
    {{! template-lint-disable no-pointer-down-event-binding }}
    {{! template-lint-disable no-invalid-interactive }}

    <ConditionalInElement
      @element={{this.modal.containerElement}}
      @inline={{@inline}}
      @append={{true}}
    >
      <this.dynamicElement
        class={{concatClass
          "modal"
          "d-modal"
          (if @inline "-inline")
          (if this.animating "is-animating")
        }}
        data-keyboard="false"
        aria-modal="true"
        role="dialog"
        aria-labelledby={{if @title "discourse-modal-title"}}
        ...attributes
        {{didInsert this.setupModal}}
        {{willDestroy this.cleanupModal}}
        {{trapTab preventScroll=false}}
      >
        <div class="d-modal__container">
          {{yield to="aboveHeader"}}

          {{#if
            (and
              (not @hideHeader)
              (or
                this.dismissable
                @title
                (has-block "headerBelowTitle")
                (has-block "headerAboveTitle")
              )
            )
          }}
            <div
              class={{concatClass "d-modal__header" @headerClass}}
              {{swipe
                didSwipe=this.handleSwipe
                didEndSwipe=this.handleSwipeEnded
                enabled=this.dismissable
              }}
            >
              {{yield to="headerAboveTitle"}}

              {{#if
                (and
                  this.site.mobileView
                  this.dismissable
                  (has-block "headerPrimaryAction")
                )
              }}
                <div class="d-modal__dismiss-action">
                  <DButton
                    @label="cancel"
                    @action={{this.handleCloseButton}}
                    @title="modal.close"
                    class="btn-transparent btn-primary d-modal__dismiss-action-button"
                  />
                </div>
              {{/if}}

              {{#if @title}}
                <div class="d-modal__title">
                  <h1
                    id="discourse-modal-title"
                    class="d-modal__title-text"
                  >{{@title}}</h1>

                  {{#if @subtitle}}
                    <p class="d-modal__subtitle-text">{{@subtitle}}</p>
                  {{/if}}

                  {{yield to="belowModalTitle"}}
                </div>
              {{/if}}
              {{yield to="headerBelowTitle"}}

              {{#if
                (and this.site.mobileView (has-block "headerPrimaryAction"))
              }}
                <div class="d-modal__primary-action">
                  {{yield to="headerPrimaryAction"}}
                </div>
              {{else if this.dismissable}}
                <DButton
                  @icon="times"
                  @action={{this.handleCloseButton}}
                  @title="modal.close"
                  class="btn-transparent modal-close"
                />
              {{/if}}
            </div>
          {{/if}}

          {{yield to="belowHeader"}}

          {{this.validateFlashType @flashType}}
          {{#if @flash}}
            <div
              id="modal-alert"
              role="alert"
              class={{concatClass
                "alert"
                (if @flashType (concat "alert-" @flashType))
              }}
            >
              {{~@flash~}}
            </div>
          {{/if}}

          <div class={{concatClass "d-modal__body" @bodyClass}} tabindex="-1">
            {{#if (has-block "body")}}
              {{yield to="body"}}
            {{else}}
              {{yield}}
            {{/if}}
          </div>

          {{#if (has-block "footer")}}
            <div class="d-modal__footer">
              {{yield to="footer"}}
            </div>
          {{/if}}

          {{yield to="belowFooter"}}
        </div>
      </this.dynamicElement>
      {{#unless @inline}}
        <div
          class="d-modal__backdrop"
          {{swipe
            didSwipe=this.handleSwipe
            didEndSwipe=this.handleSwipeEnded
            enabled=this.dismissable
          }}
          {{on "click" this.handleWrapperClick}}
        ></div>
      {{/unless}}
    </ConditionalInElement>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
